<template>
    <div class="mechanic-location-main">
        <div class="skeleton-container" v-if="loading">
            <div v-for="n in 3" style="width: 60%; margin-bottom: 4px" :key="n">
                <div class="skeleton-square" style="height: 40px" />
            </div>
        </div>
        <ErrorMessage v-else-if="error" :error-message="$t('shared.Oops, something went wrong')" />
        <div v-else-if="isEmpty" class="error-message">
            <h4>{{ $t("mechanic.No installation location found") }}</h4>
            <QitButton @click="goBack" :label="$t('shared.Back')" />
        </div>
        <LazyLoadScrollView
            v-else
            class="selection-list"
            :reach-bottom-text="$t('mechanic.Loading installation locations')"
            @load-more="onLoadMore"
        >
            <div>
                <TilesGrid
                    :tiles="tiles"
                    always-single-col
                    :grid-gap="false"
                    @link-clicked="linkClicked"
                    @select="emitSelect"
                />
            </div>
        </LazyLoadScrollView>
    </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { useMechanicRoute } from "@/abilities/mechanic/composable/mechanic-route";
import { router } from "@/app/router.ts";
import QitButton from "@/shared/components/buttons/qit-button.vue";
import ErrorMessage from "@/shared/components/placeholder/error-message.vue";
import { loadMore } from "@/shared/components/scroll/lazy-loading-fetch";
import LazyLoadScrollView from "@/shared/components/scroll/lazy-loading-scroll-view.vue";
import TilesGrid from "@/shared/components/tiles/tiles-grid.vue";
import { QualificationLevelSelectionResult } from "@/shared/environment/ability.types";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { MechanicQueryResult } from "@/shared/services/graphql/generated/admin-graph-types";
import { getDataQueryLanguages } from "@/shared/services/providers/language-provider";
import { computed, watch } from "vue";
import { useMechanicLocationDataSource } from "./mechanic-location-for-qualification.model";

const props = defineProps<{
    selectionData: QualificationLevelSelectionResult;
    isLastLevel: boolean;
    emitLastLevelSelection?: boolean;
}>();

const emit = defineEmits<{
    (e: "select", ...params: any): void;
    (e: "linkClicked"): void;
    (e: "back"): void;
}>();

const goBack = () => {
    emit("back");
};

const emitSelect = (...params: any) => {
    if (props.emitLastLevelSelection) {
        // Merge accumulated selectionData (product/asset) with the tile's mechanic selection
        emit("select", { ...props.selectionData, ...params[0] });
    } else {
        emit("select", ...params);
    }
};

const linkClicked = () => {
    emit("linkClicked");
};

const { loading, error, fetchMore, mechanics, tiles, paths } = useMechanicLocationDataSource(
    computed(() => {
        return {
            first: 10,
            acceptedLanguages: getDataQueryLanguages(),
            articleId: props.selectionData[CommonRouteAliases.commonArticle],
            product: props.selectionData[CommonRouteAliases.product],
            asset: props.selectionData[CommonRouteAliases.asset],
            mechanicId: props.selectionData[CommonRouteAliases.mechanicArticle],
        };
    }),
    // Emit selection (rather than navigate) when not the last level, or when the parent
    // wants to intercept last-level selection (e.g. combined-view widget)
    !props.isLastLevel || !!props.emitLastLevelSelection
);

const lazyLoadAmount = 10;

const isEmpty = computed(() => tiles.value.length <= 0);

const onLoadMore = (callBackAfterLoaded: (hasMoreElements: boolean) => void) => {
    if (!mechanics.value) {
        // Noch nicht initialisiert. Also wahrscheinlich gibt es schon was zum Nachladen
        callBackAfterLoaded(true);
        return;
    }
    const mechanicsValue = mechanics.value as MechanicQueryResult;
    const lastMechanicValue = mechanicsValue.mechanics[mechanicsValue.mechanics.length - 1];
    if (!lastMechanicValue || !lastMechanicValue.cursor) {
        callBackAfterLoaded(false);
        return;
    }
    loadMore(fetchMore, lastMechanicValue.cursor, lazyLoadAmount).then((data) => {
        const isAtEnd = data?.data.mechanics.mechanics.length == 0;
        callBackAfterLoaded(!isAtEnd);
    });
    return;
};

const { getAbsolutePathToMechanicLocation } = useMechanicRoute();

watch(loading, () => {
    // Prüfe, ob nur Eintrag vorhanden ist und falls ja, dann springe dort gleich hin
    if (props.isLastLevel && !props.emitLastLevelSelection) {
        if (loading.value === false) {
            if (paths.value.length === 1) {
                router.push(
                    getAbsolutePathToMechanicLocation(
                        props.selectionData[CommonRouteAliases.product],
                        props.selectionData[CommonRouteAliases.asset],
                        paths.value[0]
                    )
                );
            }
        }
    }
});
</script>

<style lang="scss">
.installation-location-selection .wizard-title {
    background-color: $white !important;
}

.mechanic-location-main .tile-grid div:first-of-type .selection-links {
    border-top: none;
}
</style>

<style lang="scss" scoped>
.mechanic-location-main {
    height: 100%;

    .tile-grid {
        background-color: $white;
    }
}

.skeleton-container {
    display: flex;
    flex-direction: column;
    padding-top: $spacing-l;
    padding-left: $spacing-xl;
    gap: $spacing-l;
}

.error-message {
    padding: $spacing-xl;
}
</style>
